import React, { useState } from "react";
import useSWR from "swr";
import api, { money } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import BookingPlanCell from "@/components/BookingPlanCell";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { BOOKING, ROOM_TYPE } from "@/lib/labels";

const fetcher = (u) => api.get(u).then((r) => r.data);
const empty = { tour_id: "", first_name: "", last_name: "", email: "", phone: "", country: "", room_type: "double", booking_status: "lead", price: 0 };

export default function Participants() {
  const { data: tours } = useSWR("/tours", fetcher);
  const { data, mutate } = useSWR("/participants", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const submit = async () => {
    if (!form.tour_id) return toast.error("Selecciona un tour");
    try {
      await api.post("/participants", { ...form, price: Number(form.price) });
      toast.success("Reserva creada");
      setForm(empty); setOpen(false); mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const autofillFromEmail = async () => {
    const em = form.email?.trim().toLowerCase();
    if (!em) return;
    try {
      const { data } = await api.get(`/clients/by-email/${encodeURIComponent(em)}`);
      setForm((f) => ({
        ...f,
        first_name: data.first_name || f.first_name,
        last_name: data.last_name || f.last_name,
        phone: data.phone || f.phone,
        country: data.country || f.country,
      }));
      toast.success(`Cliente existente: ${data.tours_count} tour(s) previos`);
    } catch {
      // no client — silent
    }
  };

  const updateStatus = async (id, booking_status) => {
    await api.patch(`/participants/${id}`, { booking_status });
    mutate();
  };

  const tourName = (id) => tours?.find((t) => t.id === id)?.name || "—";

  const filtered = (data || []).filter((p) => {
    const s = `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase();
    return (q === "" || s.includes(q.toLowerCase())) && (filterStatus === "all" || p.booking_status === filterStatus);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Reservas por tour</h1>
          <p className="text-slate-500 mt-1">{data?.length || 0} reservas · un cliente puede tener varias.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-participant-btn" className="bg-[#026FCB] hover:bg-[#0358a5]"><Plus size={16} className="mr-2" />Nueva reserva</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nueva reserva</DialogTitle>
              <p className="text-sm text-slate-500 mt-1">Si el cliente ya ha viajado antes, escribe su email y pulsa fuera para autocompletar sus datos.</p>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tour</Label>
                <Select value={form.tour_id} onValueChange={(v) => {
                  const tp = (tours || []).find((t) => t.id === v)?.price_per_participant;
                  setForm((f) => ({
                    ...f,
                    tour_id: v,
                    price: (!f.price || Number(f.price) === 0) && tp ? tp : f.price,
                  }));
                }}>
                  <SelectTrigger data-testid="part-tour"><SelectValue placeholder="Selecciona tour…" /></SelectTrigger>
                  <SelectContent>{(tours || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Nombre</Label><Input data-testid="part-first" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} /></div>
              <div><Label>Apellidos</Label><Input data-testid="part-last" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} /></div>
              <div><Label>Email</Label><Input data-testid="part-email" type="email" value={form.email} onBlur={autofillFromEmail} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
              <div><Label>País</Label><Input value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} /></div>
              <div><Label>Habitación</Label>
                <Select value={form.room_type} onValueChange={(v) => setForm({...form, room_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(ROOM_TYPE).map((r) => <SelectItem key={r} value={r}>{ROOM_TYPE[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Precio pactado</Label><Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} /></div>
              <div><Label>Estado</Label>
                <Select value={form.booking_status} onValueChange={(v) => setForm({...form, booking_status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["lead","interested","deposit_pending","deposit_paid","confirmed","partial_paid","fully_paid","cancelled","waitlist"].map((s) => <SelectItem key={s} value={s}>{BOOKING[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button data-testid="part-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submit}>Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="part-search" className="pl-9" placeholder="Buscar por nombre o email…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {["lead","interested","deposit_pending","deposit_paid","confirmed","partial_paid","fully_paid","cancelled","refunded","waitlist"].map((s) => <SelectItem key={s} value={s}>{BOOKING[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="overflow-hidden"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-100 dark:bg-[#1A2336]">
            <tr>
              <th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-4 py-3">Tour</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">País</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-left px-4 py-3">Plan de pagos</th>
              <th className="text-right px-4 py-3">Precio</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`participant-row-${p.id}`} className="border-t border-slate-100 dark:border-[#25334D]/50 hover:bg-slate-50 dark:hover:bg-[#1A2336]/60">
                <td className="px-4 py-3 font-medium">{p.first_name} {p.last_name}</td>
                <td className="px-4 py-3 text-slate-500">{tourName(p.tour_id)}</td>
                <td className="px-4 py-3 text-slate-500">{p.email}</td>
                <td className="px-4 py-3 text-slate-500">{p.country || "—"}</td>
                <td className="px-4 py-3">
                  <Select value={p.booking_status} onValueChange={(v) => updateStatus(p.id, v)}>
                    <SelectTrigger className="h-8 w-40" data-testid={`part-status-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["lead","interested","deposit_pending","deposit_paid","confirmed","partial_paid","fully_paid","cancelled","refunded","waitlist"].map((s) => <SelectItem key={s} value={s}>{BOOKING[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3"><BookingPlanCell participantId={p.id} /></td>
                <td className="px-4 py-3 text-right mono">{money(Number(p.price) || (tours || []).find((t) => t.id === p.tour_id)?.price_per_participant || 0)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">Sin reservas.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
